import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { StatusMessage } from "../../components/ui/StatusMessage";
import { useAuth } from "../../hooks/useAuth";

export function LoginPage() {
  const navigate = useNavigate();
  const { isConfigured, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (!email.trim() || !password) {
      setFormError("Email and password are required.");
      return;
    }

    setIsSubmitting(true);

    try {
      await signIn({ email, password });
      navigate("/", { replace: true });
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to sign in with those details.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div>
        <p className="eyebrow">Welcome back</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-normal text-ink">
          Sign in to CoachGrid
        </h1>
        <p className="mt-3 text-base leading-7 text-graphite/70">
          Open your coaching workspace and continue where you left off.
        </p>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        {!isConfigured ? (
          <StatusMessage
            message="Supabase environment variables are missing. Add them before signing in."
            tone="error"
          />
        ) : null}

        {formError ? <StatusMessage message={formError} tone="error" /> : null}

        <Input
          autoComplete="email"
          label="Email"
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          value={email}
        />
        <Input
          autoComplete="current-password"
          label="Password"
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          value={password}
        />

        <Button fullWidth isLoading={isSubmitting} type="submit">
          Sign in
        </Button>
      </form>

      <div className="mt-6 flex flex-col gap-3 text-sm text-graphite/70 sm:flex-row sm:items-center sm:justify-between">
        <Link className="link" to="/forgot-password">
          Forgot password?
        </Link>
        <p>CoachGrid access is invite only.</p>
      </div>
    </AuthLayout>
  );
}
